import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { formatBytes } from '../utils/crypto';
import { formatDateTime } from '../utils/formatDate';
import { apiListVersions, apiDownloadVersion, apiRestoreVersion, apiDeleteVersion } from '../utils/api';
import { messaggioErrore } from '../utils/messaggioErrore';

function decodeVersionName(encryptedNameStr, fallback) {
  if (!encryptedNameStr) return fallback;
  if (encryptedNameStr.startsWith('plain:')) {
    try { return decodeURIComponent(escape(atob(encryptedNameStr.slice(6)))); } catch { return fallback; }
  }
  return fallback;
}

// Opened from a file's context menu — lists archived versions of that file's
// content (created whenever the file is replaced or overwritten via WebDAV)
// with download/restore/delete-version actions. Versions older than 1 year
// are pruned server-side (see backend/utils/fileVersions.js).
export default function VersionHistoryModal({ file, onClose, onRestored }) {
  const { t, i18n } = useTranslation('share');
  const { encKey } = useAuth();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [busyId, setBusyId]     = useState(null);

  useEffect(() => {
    if (!file) return;
    setLoading(true); setError(''); setVersions([]);
    apiListVersions(file.id)
      .then(setVersions)
      .catch(e => setError(messaggioErrore(e, t)))
      .finally(() => setLoading(false));
  }, [file]);

  async function handleDownload(v) {
    setBusyId(v.id + 'd');
    try {
      const { blob, isEncrypted } = await apiDownloadVersion(file.id, v.id);
      let finalBlob = blob, finalName = decodeVersionName(v.encryptedName, v.plainName || file.filename);
      if (isEncrypted) {
        if (!encKey) throw new Error(t('versionhistory_no_key_error'));
        // Il formato viaggia con la versione (colonna format_version, aggiunta
        // il 2026-09-16): una versione archiviata da un file a blocchi va letta
        // a blocchi. Stessa scelta del vault (useFiles.downloadFile), stesso
        // lettore; formato assente = il formato di sempre. Al 18/09/2026
        // nessuna versione archiviata e' a blocchi (verificato sui byte), ma
        // la prima sovrascrittura di un file a blocchi ne creera' una.
        if (Number(v.formatVersion) === 2) {
          const { decifraV2InMemoria } = await import('../utils/cifraturaBlocchi');
          finalBlob = await decifraV2InMemoria(blob, encKey, v.mimeType || file.mimeType || '');
        } else {
          const { decryptFile } = await import('../utils/crypto');
          const r = await decryptFile(encKey, blob, v.iv, v.encryptedName);
          finalBlob = r.plainBlob; finalName = r.filename || finalName;
        }
      }
      const url = URL.createObjectURL(finalBlob);
      const a = document.createElement('a'); a.href = url; a.download = finalName;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { alert(t('versionhistory_download_error_prefix') + messaggioErrore(e, t)); }
    finally { setBusyId(null); }
  }

  async function handleRestore(v) {
    if (!window.confirm(t('versionhistory_restore_confirm'))) return;
    setBusyId(v.id + 'r');
    try {
      await apiRestoreVersion(file.id, v.id);
      onRestored?.();
      onClose();
    } catch (e) { alert(t('versionhistory_restore_error_prefix') + messaggioErrore(e, t)); }
    finally { setBusyId(null); }
  }

  async function handleDelete(v) {
    if (!window.confirm(t('versionhistory_delete_confirm'))) return;
    setBusyId(v.id + 'x');
    try {
      await apiDeleteVersion(file.id, v.id);
      setVersions(prev => prev.filter(x => x.id !== v.id));
    } catch (e) { alert(t('versionhistory_delete_error_prefix') + messaggioErrore(e, t)); }
    finally { setBusyId(null); }
  }

  if (!file) return null;

  return (
    <div className="vz-modal-overlay" onClick={onClose}>
      <div className="vz-modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <h3 className="vz-modal-title">{t('versionhistory_title')}</h3>
        <p style={{ fontSize: 13, color: 'var(--text3)', margin: '-6px 0 10px' }}>
          <strong style={{ color: 'var(--text)' }}>{file.filename}</strong> · {t('versionhistory_kept_note')}
        </p>

        {loading && <p style={{ fontSize: 13, color: 'var(--text3)' }}>{t('versionhistory_loading')}</p>}
        {error && <p className="vz-modal-err">{error}</p>}
        {!loading && !error && versions.length === 0 && (
          <p style={{ fontSize: 13, color: 'var(--text3)' }}>
            {t('versionhistory_empty')}
          </p>
        )}
        {!loading && versions.length > 0 && (
          <div className="move-folder-list" style={{ maxHeight: 340, overflowY: 'auto' }}>
            {versions.map(v => (
              <div key={v.id} className="move-folder-item" style={{ cursor: 'default', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {decodeVersionName(v.encryptedName, v.plainName || file.filename)}
                  </div>
                  <div style={{ fontSize: 12 }}>{formatDateTime(v.savedAt, i18n.language)}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>{formatBytes(v.size)}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button className="vz-btn vz-btn-sm vz-btn-ghost" title={t('versionhistory_download_title')} disabled={busyId === v.id + 'd'} onClick={() => handleDownload(v)}>
                    {busyId === v.id + 'd' ? '⏳' : '⬇'}
                  </button>
                  <button className="vz-btn vz-btn-sm vz-btn-ghost" disabled={busyId === v.id + 'r'} onClick={() => handleRestore(v)}>
                    {busyId === v.id + 'r' ? '⏳' : t('versionhistory_restore_btn')}
                  </button>
                  <button className="vz-btn vz-btn-sm vz-btn-red" title={t('versionhistory_delete_title')} disabled={busyId === v.id + 'x'} onClick={() => handleDelete(v)}>
                    🗑
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="vz-modal-btns">
          <button className="vz-btn vz-btn-ghost" onClick={onClose}>{t('versionhistory_close_btn')}</button>
        </div>
      </div>
    </div>
  );
}
